import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/portfolio/Navbar";
import { 
  ArrowLeft, 
  ArrowUpRight, 
  Building2, 
  Layers, 
  Globe, 
  Calendar, 
  User, 
  CheckCircle2, 
  ArrowRight,
  TrendingUp,
  Share2,
  AlertTriangle,
  Sparkles,
  ShieldCheck,
  GitBranch,
  Clock,
  Check,
  Zap,
  BarChart3,
  Search,
  Filter,
  Users,
  Target,
  FileText,
  RefreshCw,
  MousePointerClick
} from "lucide-react";

export default function MyOperatorCaseStudyPage() {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const originalTitle = document.title;
    const bestTitle = "MyOperator SEO Case Study: Rebuilding B2B SaaS Content & Organic Growth | Kanna Narendra Kumar";
    const bestDesc = "How Kanna Narendra Kumar rebuilt SEO for MyOperator—cleaning low-value content, fixing technical SEO, and driving 200% organic growth and 60% of company leads.";

    document.title = bestTitle;

    // Helper to set meta tags
    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (el) {
        el.setAttribute(attr, value);
      } else {
        const meta = document.createElement("meta");
        const isProp = selector.includes("property=");
        if (isProp) {
          const match = selector.match(/property="([^"]+)"/);
          if (match) meta.setAttribute("property", match[1]);
        } else {
          const match = selector.match(/name="([^"]+)"/);
          if (match) meta.setAttribute("name", match[1]);
        }
        meta.setAttribute(attr, value);
        document.head.appendChild(meta);
      }
    };

    // Set Meta Description
    let metaDesc = document.querySelector('meta[name="description"]');
    const originalDesc = metaDesc ? metaDesc.getAttribute("content") : "";
    setMeta('meta[name="description"]', "content", bestDesc);

    // Set Social / OG Meta Tags
    setMeta('meta[property="og:title"]', "content", bestTitle);
    setMeta('meta[property="og:description"]', "content", bestDesc);
    setMeta('meta[property="og:url"]', "content", "https://narendradigital.in/casestudy/myoperator/");
    setMeta('meta[property="og:type"]', "content", "article");
    setMeta('meta[name="twitter:title"]', "content", bestTitle);
    setMeta('meta[name="twitter:description"]', "content", bestDesc);

    // Set Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    let createdCanonical = false;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
      createdCanonical = true;
    }
    const originalCanonical = canonical ? canonical.getAttribute("href") : null;
    if (canonical) {
      canonical.setAttribute("href", "https://narendradigital.in/casestudy/myoperator/");
    }

    // Add JSON-LD Schema
    const scriptId = "myoperator-case-study-schema";
    let scriptTag = document.getElementById(scriptId);
    if (!scriptTag) {
      scriptTag = document.createElement("script");
      scriptTag.id = scriptId;
      scriptTag.type = "application/ld+json";
      scriptTag.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": "MyOperator SEO Case Study: Rebuilding B2B SaaS Content & Organic Growth",
        "description": bestDesc,
        "author": {
          "@type": "Person",
          "name": "Kanna Narendra Kumar",
          "jobTitle": "Senior SEO Specialist & AI Search Strategist",
          "url": "https://narendradigital.in/"
        },
        "publisher": {
          "@type": "Person",
          "name": "Kanna Narendra Kumar"
        },
        "mainEntityOfPage": "https://narendradigital.in/casestudy/myoperator/"
      });
      document.head.appendChild(scriptTag);
    }

    window.scrollTo(0, 0);

    return () => {
      document.title = originalTitle;
      if (metaDesc && originalDesc) metaDesc.setAttribute("content", originalDesc);
      if (canonical) {
        if (createdCanonical) {
          canonical.remove();
        } else if (originalCanonical) {
          canonical.setAttribute("href", originalCanonical);
        }
      }
      if (scriptTag) scriptTag.remove();
    };
  }, []);

  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <div className="bg-[#080c16] text-[#e2e8f0] min-h-screen font-sans selection:bg-[#3b82f6] selection:text-white antialiased relative overflow-x-hidden">
      
      {/* Main Global Navigation Bar */}
      <Navbar />

      {/* Ambient background glowing orbs */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[450px] bg-gradient-to-b from-blue-600/15 via-cyan-500/10 to-transparent blur-[120px] -z-10" />
      <div className="pointer-events-none absolute top-[900px] -left-48 w-[500px] h-[500px] bg-indigo-600/10 blur-[140px] -z-10" />
      <div className="pointer-events-none absolute top-[2200px] -right-48 w-[500px] h-[500px] bg-emerald-600/10 blur-[140px] -z-10" />

      {/* Main Article Container */}
      <main className="max-w-4xl mx-auto px-5 sm:px-8 pt-28 sm:pt-32 pb-16">

        {/* Back to Portfolio & Share Utility Bar */}
        <div className="flex items-center justify-between gap-4 mb-8 pb-3 border-b border-white/[0.08]">
          <Link
            to="/"
            className="group inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-1 text-cyan-400" />
            <span>Back to Portfolio</span>
          </Link>

          <div className="flex items-center gap-3 text-xs font-mono">
            <button
              onClick={handleShare}
              className="px-2.5 py-1 rounded-md border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-slate-300 hover:text-white transition-colors flex items-center gap-1.5"
              title="Copy Case Study URL"
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-emerald-400">Copied Link</span>
                </>
              ) : (
                <>
                  <Share2 className="w-3.5 h-3.5" />
                  <span>Share</span>
                </>
              )}
            </button>
            <a
              href="mailto:narendrakumarkanna@gmail.com"
              className="text-cyan-400 hover:text-white transition-colors flex items-center gap-1 font-semibold"
            >
              <span>Contact</span>
              <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        {/* HERO SECTION */}
        <section className="mb-14">
          
          {/* Badge Strip */}
          <div className="flex flex-wrap items-center gap-2 text-xs font-mono text-slate-400 mb-6">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-300 font-semibold shadow-inner">
              <Building2 className="w-3.5 h-3.5 text-blue-400" />
              <span>MyOperator</span>
            </div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 font-semibold">
              <Target className="w-3.5 h-3.5 text-cyan-400" />
              <span>B2B SaaS SEO</span>
            </div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 font-semibold">
              <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
              <span>+200% Organic Growth</span>
            </div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 font-semibold">
              <Sparkles className="w-3.5 h-3.5 text-purple-400" />
              <span>Content to Pipeline</span>
            </div>
          </div>

          {/* Main Title */}
          <h1 className="font-heading font-extrabold text-3xl sm:text-5xl lg:text-[44px] text-white tracking-tight leading-[1.2] mb-6">
            Turning Content Into Business Growth:{" "}
            <span className="bg-gradient-to-r from-blue-400 via-cyan-300 to-teal-300 bg-clip-text text-transparent">
              Rebuilding SEO for MyOperator
            </span>
          </h1>

          {/* Subheading in Plain, Friendly English */}
          <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-3xl mb-8">
            How I took charge of an organic growth strategy that had no dedicated SEO owner for two years, cleaned up low-value content, rebuilt the technical foundation, and turned organic search into the company's #1 lead generator.
          </p>


          {/* Author Card */}
          <div className="p-4 sm:p-5 rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur-sm shadow-xl flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3.5">
              <div className="relative">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600 via-indigo-600 to-cyan-400 text-white flex items-center justify-center font-bold text-sm shadow-md shadow-blue-500/20">
                  KN
                </div>
                <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-emerald-400 border-2 border-[#080c16] rounded-full" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-white font-semibold text-sm">Kanna Narendra Kumar</p>
                  <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-cyan-400/10 text-cyan-300 border border-cyan-400/20">SEO Lead</span>
                </div>
                <p className="text-xs text-slate-400">Senior SEO Specialist &amp; Content Strategist</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs font-mono text-slate-400 pt-3 sm:pt-0 border-t sm:border-t-0 border-white/10">
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-slate-500" />
                <span>6 min read</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5 text-slate-500" />
                <span>Cloud Communications SaaS</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-emerald-400 font-medium">Verified Results</span>
              </div>
            </div>
          </div>
        </section>

        {/* 2 STANDOUT METRICS RIBBON */}
        <section className="mb-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
            
            {/* Metric 1 */}
            <div className="p-5 sm:p-6 rounded-2xl bg-gradient-to-b from-white/[0.05] to-white/[0.01] border border-white/10 hover:border-cyan-500/40 transition-all duration-300">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-mono uppercase tracking-wider text-slate-400 font-medium">Organic Growth</span>
                <div className="w-7 h-7 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 flex items-center justify-center">
                  <TrendingUp className="w-3.5 h-3.5" />
                </div>
              </div>
              <div className="font-heading font-extrabold text-3xl sm:text-4xl text-white tracking-tight">
                +200%
              </div>
              <p className="text-xs text-slate-400 mt-1.5 leading-snug">
                Sustained organic traffic expansion across high-value business topics.
              </p>
              <div className="mt-3 pt-2.5 border-t border-white/5 flex items-center gap-1 text-[11px] font-mono text-cyan-400">
                <CheckCircle2 className="w-3 h-3" />
                <span>Steady Multi-Year Growth</span>
              </div>
            </div>

            {/* Metric 2 */}
            <div className="p-5 sm:p-6 rounded-2xl bg-gradient-to-b from-white/[0.05] to-white/[0.01] border border-white/10 hover:border-emerald-500/40 transition-all duration-300">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-mono uppercase tracking-wider text-slate-400 font-medium">Company Leads</span>
                <div className="w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center">
                  <Target className="w-3.5 h-3.5" />
                </div>
              </div>
              <div className="font-heading font-extrabold text-3xl sm:text-4xl text-emerald-400 tracking-tight">
                60%
              </div>
              <p className="text-xs text-slate-400 mt-1.5 leading-snug">
                Organic search became the #1 inbound customer acquisition channel.
              </p>
              <div className="mt-3 pt-2.5 border-t border-white/5 flex items-center gap-1 text-[11px] font-mono text-emerald-400">
                <Zap className="w-3 h-3" />
                <span>#1 Lead Source</span>
              </div>
            </div>

          </div>
        </section>

        {/* SECTION 1: OVERVIEW */}
        <section className="scroll-mt-24 mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-cyan-400" />
            <h2 className="text-xs font-mono uppercase tracking-widest text-cyan-400 font-bold">
              01 / Overview
            </h2>
          </div>

          <div className="space-y-4 text-base leading-relaxed text-slate-300 mb-8">
            <p>
              When I started managing SEO for <strong className="text-white font-semibold">MyOperator</strong>, the website had been operating without a dedicated SEO specialist for nearly <strong className="text-white font-semibold">two years</strong>.
            </p>
          </div>

          {/* The Situation Banner */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-transparent border border-amber-500/30 p-6 sm:p-7 shadow-lg mb-8">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/30 text-amber-300 flex items-center justify-center flex-shrink-0 mt-0.5">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <div className="space-y-2">
                <span className="text-xs font-mono uppercase tracking-wider text-amber-400 font-bold px-2 py-0.5 rounded bg-amber-400/10 border border-amber-400/20 inline-block">
                  The Problem
                </span>
                <h3 className="font-heading font-bold text-lg sm:text-xl text-white">
                  Content Was Being Written for Numbers, Not for Customers
                </h3>
                <p className="text-sm sm:text-base text-amber-100/90 leading-relaxed">
                  Content decisions were based purely on search volume and keyword difficulty scores from tools. Technical SEO and actual buyer intent were ignored. A huge volume of articles was being published, but very few had any connection to what MyOperator actually sells.
                </p>
              </div>
            </div>
          </div>

          <div className="p-5 rounded-xl bg-white/[0.02] border border-white/10 text-sm leading-relaxed text-slate-300">
            <p>
              My goal was clear: <strong className="text-white">rebuild the SEO foundation, remove useless content, and shift our focus from chasing vanity traffic to attracting real business buyers who need cloud phone systems.</strong>
            </p>
          </div>
        </section>

        {/* SECTION 2: THE CHALLENGE */}
        <section className="scroll-mt-24 mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-cyan-400" />
            <h2 className="text-xs font-mono uppercase tracking-widest text-cyan-400 font-bold">
              02 / The Challenge
            </h2>
          </div>

          <h3 className="font-heading font-bold text-2xl sm:text-3xl text-white tracking-tight mb-4">
            Lots of Content, But No Clear Business Value
          </h3>
          <p className="text-base text-slate-300 leading-relaxed mb-6">
            The main challenge was never about producing more articles. It was deciding what content was actually worth keeping, what needed a complete revamp, and how SEO could directly bring in qualified business leads.
          </p>

          {/* 4 Challenge Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            
            <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center font-mono text-xs font-bold mb-3">
                01
              </div>
              <h4 className="font-heading font-bold text-base text-white mb-2">
                2 Years Without Dedicated SEO
              </h4>
              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                Articles were written without an overarching SEO strategy or technical checks. Many pages generated zero organic visits.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="w-8 h-8 rounded-lg bg-cyan-500/10 text-cyan-400 flex items-center justify-center font-mono text-xs font-bold mb-3">
                02
              </div>
              <h4 className="font-heading font-bold text-base text-white mb-2">
                Ignoring Business Intent
              </h4>
              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                Topics were picked just because they had high search volume. Readers were visiting for random topics and leaving immediately without knowing what MyOperator does.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="w-8 h-8 rounded-lg bg-indigo-500/10 text-indigo-400 flex items-center justify-center font-mono text-xs font-bold mb-3">
                03
              </div>
              <h4 className="font-heading font-bold text-base text-white mb-2">
                Technical SEO Debt
              </h4>
              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                Weak canonical tags, disorganized sitemaps, indexation issues, and missed internal links made it hard for search engines to crawl smoothly.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center font-mono text-xs font-bold mb-3">
                04
              </div>
              <h4 className="font-heading font-bold text-base text-white mb-2">
                Zero Lead Tracking on Articles
              </h4>
              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                There were no call-to-action buttons or conversion goals set up inside articles to measure if readers were actually becoming software customers.
              </p>
            </div>

          </div>
        </section>

        {/* SECTION 3: MY APPROACH */}
        <section className="scroll-mt-24 mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-cyan-400" />
            <h2 className="text-xs font-mono uppercase tracking-widest text-cyan-400 font-bold">
              03 / My Strategy
            </h2>
          </div>

          <h3 className="font-heading font-bold text-2xl sm:text-3xl text-white tracking-tight mb-4">
            The 4-Pillar Roadmap: Clean → Fix → Target → Convert
          </h3>
          <p className="text-base text-slate-300 leading-relaxed mb-6">
            Instead of simply publishing more articles, I organized my work into four clear, sequential stages:
          </p>

          {/* 4-Step Stepper Cards */}
          <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/10 shadow-lg">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { 
                  step: "01", 
                  name: "Clean", 
                  title: "Remove Clutter",
                  desc: "Audited the content library and removed articles with zero traffic and no business relevance." 
                },
                { 
                  step: "02", 
                  name: "Fix", 
                  title: "Fix Technical Debt",
                  desc: "Fixed canonical tags, sitemaps, crawling blocks, and internal link paths." 
                },
                { 
                  step: "03", 
                  name: "Target", 
                  title: "Business-Intent Clusters",
                  desc: "Built topic clusters and long-tail keywords around what buyers actually search for." 
                },
                { 
                  step: "04", 
                  name: "Convert", 
                  title: "Lead Generation",
                  desc: "Added clear CTAs and conversion tracking to turn readers into software demos and signups." 
                },
              ].map((item, idx) => (
                <div key={idx} className="relative p-4 rounded-xl bg-white/[0.03] border border-white/5 group hover:border-cyan-500/40 transition-colors">
                  <span className="text-xs font-mono text-cyan-400 font-bold block mb-1">Step {item.step}</span>
                  <span className="text-base font-bold text-white block mb-1">{item.name}</span>
                  <span className="text-xs font-semibold text-slate-300 block mb-2">{item.title}</span>
                  <span className="text-xs text-slate-400 block leading-relaxed">{item.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 4: WHAT I WORKED ON (8 Projects) */}
        <section className="scroll-mt-24 mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-cyan-400" />
            <h2 className="text-xs font-mono uppercase tracking-widest text-cyan-400 font-bold">
              04 / What I Worked On
            </h2>
          </div>

          <h3 className="font-heading font-bold text-2xl sm:text-3xl text-white tracking-tight mb-8">
            Eight Core Initiatives That Drove +200% Organic Growth
          </h3>

          <div className="space-y-6">

            {/* 01 */}
            <article className="p-6 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-400 font-mono text-xs font-bold flex items-center justify-center">
                  01
                </span>
                <h4 className="font-heading font-bold text-lg sm:text-xl text-white">
                  Removed Low-Value Content
                </h4>
              </div>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-4">
                I conducted a detailed content audit to find articles that were bringing no traffic and had zero connection to our business. We evaluated every page based on traffic, search intent, business relevance, and content quality.
              </p>
              <div className="p-3.5 rounded-xl bg-white/[0.03] border border-white/5 text-xs sm:text-sm text-cyan-300">
                <strong>Result:</strong> Removed and consolidated junk content, saving crawl budget and allowing the team to focus on high-impact pages.
              </div>
            </article>

            {/* 02 */}
            <article className="p-6 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 font-mono text-xs font-bold flex items-center justify-center">
                  02
                </span>
                <h4 className="font-heading font-bold text-lg sm:text-xl text-white">
                  Rebuilt Technical SEO Foundation
                </h4>
              </div>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-4">
                Technical SEO had been neglected, so I conducted a full technical audit across the website:
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs font-mono mb-2">
                {[
                  "Canonical Tags Fixed",
                  "XML Sitemaps Rebuilt",
                  "Indexation Fixed",
                  "Crawl Budget Optimized",
                  "Clean URL Structure",
                  "Broken Links Removed",
                  "Server Header Audits",
                  "Mobile Page Speed"
                ].map((item, idx) => (
                  <div key={idx} className="p-2 rounded-lg bg-white/[0.03] border border-white/5 flex items-center gap-1.5 text-slate-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </article>

            {/* 03 */}
            <article className="p-6 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-8 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 font-mono text-xs font-bold flex items-center justify-center">
                  03
                </span>
                <h4 className="font-heading font-bold text-lg sm:text-xl text-white">
                  Built Business-Intent Topic Clusters
                </h4>
              </div>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-4">
                Instead of only looking at search volume, I analyzed competitors to find the topics that were driving real paying customers:
              </p>
              <ul className="space-y-2 text-xs sm:text-sm text-slate-300 pl-2">
                <li className="flex items-start gap-2">
                  <span className="text-cyan-400 font-bold">•</span>
                  <span>Which high-intent keywords competitors were targeting for IVR, virtual numbers, and call centers.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-400 font-bold">•</span>
                  <span>Where competitors had content gaps that MyOperator could dominate.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-400 font-bold">•</span>
                  <span>Organized related topics into clear clusters to build domain authority.</span>
                </li>
              </ul>
            </article>

            {/* 04 */}
            <article className="p-6 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-8 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-400 font-mono text-xs font-bold flex items-center justify-center">
                  04
                </span>
                <h4 className="font-heading font-bold text-lg sm:text-xl text-white">
                  Targeted Long-Tail Keyword Opportunities
                </h4>
              </div>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-4">
                I looked for long-tail keywords where the website already had some search visibility. I used a two-part rule:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm mb-2">
                <div className="p-3.5 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-200">
                  <strong className="block font-bold text-white mb-1">Existing Article with Potential?</strong>
                  <span>Revamp and upgrade the article with updated data, better structure, and fresh answers.</span>
                </div>
                <div className="p-3.5 rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-200">
                  <strong className="block font-bold text-white mb-1">No Relevant Article Exists?</strong>
                  <span>Create a brand-new, high-quality guide specifically answering that search intent.</span>
                </div>
              </div>
            </article>

            {/* 05 */}
            <article className="p-6 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-mono text-xs font-bold flex items-center justify-center">
                  05
                </span>
                <h4 className="font-heading font-bold text-lg sm:text-xl text-white">
                  Created an SEO Content Checklist
                </h4>
              </div>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-4">
                I created a standard SEO checklist for the content writers so SEO was planned before writing, not tacked on at the end:
              </p>
              <div className="flex flex-wrap gap-2 text-xs font-mono text-slate-300">
                {[
                  "Search Intent Match",
                  "Clear Headings (H2/H3)",
                  "Internal Link Targets",
                  "Unique Meta Descriptions",
                  "Call-to-Action Placement",
                  "Schema Structured Data",
                  "Clean Image Alt Tags"
                ].map((item, idx) => (
                  <span key={idx} className="px-2.5 py-1 rounded bg-white/[0.04] border border-white/10">
                    ✓ {item}
                  </span>
                ))}
              </div>
            </article>

            {/* 06 */}
            <article className="p-6 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-400 font-mono text-xs font-bold flex items-center justify-center">
                  06
                </span>
                <h4 className="font-heading font-bold text-lg sm:text-xl text-white">
                  Strengthened Internal Linking
                </h4>
              </div>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-3">
                Instead of using generic link text like "click here" or "learn more", I implemented natural, descriptive anchor text linking informative blog posts directly to our core product pages.
              </p>
              <p className="text-xs sm:text-sm text-slate-400">
                This helped Google understand the relationship between topics and passed ranking authority directly to high-converting product pages.
              </p>
            </article>

            {/* 07 */}
            <article className="p-6 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-mono text-xs font-bold flex items-center justify-center">
                  07
                </span>
                <h4 className="font-heading font-bold text-lg sm:text-xl text-white">
                  Connected SEO Directly with Conversions
                </h4>
              </div>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-3">
                Organic traffic is only useful if it helps the business grow. I added relevant call-to-action (CTA) banners inside articles and set up tracking to measure demo requests and free trial signups.
              </p>
              <div className="p-3.5 rounded-xl bg-white/[0.03] border border-white/5 text-xs sm:text-sm text-slate-300 italic">
                We shifted the discussion from <strong className="text-white">"How much traffic did this article get?"</strong> to <strong className="text-cyan-300">"How many customer signups did this article generate?"</strong>
              </div>
            </article>

            {/* 08 */}
            <article className="p-6 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 font-mono text-xs font-bold flex items-center justify-center">
                  08
                </span>
                <h4 className="font-heading font-bold text-lg sm:text-xl text-white">
                  Used Google Search Console for Continuous Growth
                </h4>
              </div>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-4">
                Instead of guessing what to write next, I used Google Search Console (GSC) data to spot keywords that were already starting to get impressions:
              </p>
              
              {/* Continuous Loop Box */}
              <div className="p-4 rounded-xl bg-[#050811] border border-white/10 font-mono text-xs text-center text-cyan-300">
                GSC Data ➔ Spot Emerging Queries ➔ Update Content ➔ Track Growth ➔ Repeat
              </div>
            </article>

          </div>
        </section>

        {/* SECTION 5: THE SEO TRANSFORMATION (Before vs After) */}
        <section className="scroll-mt-24 mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-cyan-400" />
            <h2 className="text-xs font-mono uppercase tracking-widest text-cyan-400 font-bold">
              05 / The Transformation
            </h2>
          </div>

          <h3 className="font-heading font-bold text-2xl sm:text-3xl text-white tracking-tight mb-6">
            The Shift: Before vs. After
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {/* Before */}
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10">
              <span className="text-xs font-mono uppercase text-red-400 font-bold block mb-3">
                Before: Traffic-Only Thinking
              </span>
              <ul className="space-y-3 text-xs sm:text-sm text-slate-400 font-mono">
                <li className="flex items-start gap-2">
                  <span className="text-red-400">✗</span>
                  <span>Picked keywords only by search volume &amp; difficulty</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400">✗</span>
                  <span>Massive content library with zero traffic</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400">✗</span>
                  <span>Ignored technical SEO issues</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400">✗</span>
                  <span>Random internal links with generic anchor text</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400">✗</span>
                  <span>No conversion tracking on blog posts</span>
                </li>
              </ul>
            </div>

            {/* After */}
            <div className="p-6 rounded-2xl bg-cyan-500/10 border border-cyan-500/30">
              <span className="text-xs font-mono uppercase text-cyan-300 font-bold block mb-3">
                After: Business-Driven Growth
              </span>
              <ul className="space-y-3 text-xs sm:text-sm text-cyan-100/90 font-mono">
                <li className="flex items-start gap-2">
                  <span className="text-cyan-400">✓</span>
                  <span>Built topic clusters based on what buyers search for</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-400">✓</span>
                  <span>Cleaned up low-value content to focus on quality</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-400">✓</span>
                  <span>Solid technical SEO foundation and clean indexing</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-400">✓</span>
                  <span>Strategic internal linking to high-intent product pages</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-400">✓</span>
                  <span>Continuous optimization using Google Search Console</span>
                </li>
              </ul>
            </div>

          </div>
        </section>

        {/* SECTION 6: KEY FOCUS AREAS */}
        <section className="scroll-mt-24 mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-cyan-400" />
            <h2 className="text-xs font-mono uppercase tracking-widest text-cyan-400 font-bold">
              06 / Core Focus Areas
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10">
              <h4 className="text-xs font-mono uppercase tracking-wider text-white font-bold mb-1">Technical SEO</h4>
              <p className="text-xs text-slate-400">Canonicals · Sitemaps · Crawlability · Indexation</p>
            </div>
            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10">
              <h4 className="text-xs font-mono uppercase tracking-wider text-white font-bold mb-1">Content Strategy</h4>
              <p className="text-xs text-slate-400">Content Audit · Topic Clusters · Revamps</p>
            </div>
            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10">
              <h4 className="text-xs font-mono uppercase tracking-wider text-white font-bold mb-1">Keyword Strategy</h4>
              <p className="text-xs text-slate-400">Business Intent · Long-Tail Queries · GSC Data</p>
            </div>
            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10">
              <h4 className="text-xs font-mono uppercase tracking-wider text-white font-bold mb-1">Internal Linking</h4>
              <p className="text-xs text-slate-400">Contextual Anchors · Topic Hierarchy · Product Links</p>
            </div>
            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 sm:col-span-2 lg:col-span-2">
              <h4 className="text-xs font-mono uppercase tracking-wider text-white font-bold mb-1">Conversion SEO</h4>
              <p className="text-xs text-slate-400">CTA Placement · Tracking Actions · Lead Quality</p>
            </div>
          </div>
        </section>

        {/* SECTION 7: OUTCOME & LESSONS */}
        <section className="scroll-mt-24 mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-cyan-400" />
            <h2 className="text-xs font-mono uppercase tracking-widest text-cyan-400 font-bold">
              07 / The Outcome
            </h2>
          </div>

          <h3 className="font-heading font-bold text-2xl sm:text-3xl text-white tracking-tight mb-4">
            From Vanity Traffic to Sustainable Revenue
          </h3>
          <p className="text-base text-slate-300 leading-relaxed mb-6">
            The biggest transformation was shifting from a <em>"publish more articles"</em> mindset to a <em>"generate qualified business leads"</em> strategy.
          </p>

          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 text-sm leading-relaxed text-slate-300 space-y-3 mb-8">
            <p>
              Every major SEO decision was connected directly to:
            </p>
            <div className="p-3.5 rounded-xl bg-[#050811] border border-white/5 font-mono text-xs text-cyan-300 text-center">
              Search Intent ➔ Business Relevance ➔ Technical Foundation ➔ Content ➔ Internal Linking ➔ Conversion
            </div>
            <p className="pt-2">
              Instead of continually adding content clutter, we built the right content, improved articles that had potential, pruned what didn't work, and made organic search the <strong>#1 source of inbound leads for the entire company</strong>.
            </p>
          </div>
        </section>

        {/* EXPLORE OTHER CASE STUDIES */}
        <section className="mb-14 pt-8 border-t border-white/[0.08]">
          <h4 className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-4 font-semibold">
            Explore Other Case Studies
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            
            <Link
              to="/casestudy/seismic/"
              className="p-5 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-cyan-500/40 transition-all group flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between text-xs font-mono text-cyan-400 mb-2">
                  <span>Seismic Software</span>
                  <span className="px-2 py-0.5 rounded bg-cyan-400/10 border border-cyan-400/20 text-[10px]">Enterprise CMS</span>
                </div>
                <h5 className="font-heading font-bold text-white text-base group-hover:text-cyan-300 transition-colors mb-2">
                  WordPress to Sitecore Migration &amp; AI-Search Lift
                </h5>
                <p className="text-xs text-slate-400 line-clamp-2">
                  How I stopped an initial 20% traffic drop, fully recovered rankings in 90 days, and lifted AI citations by 120%.
                </p>
              </div>
              <div className="mt-4 flex items-center gap-1.5 text-xs font-mono text-cyan-400 font-semibold">
                <span>Read Case Study</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link
              to="/casestudy/workhours/"
              className="p-5 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-cyan-500/40 transition-all group flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between text-xs font-mono text-cyan-400 mb-2">
                  <span>Workhours → Trydo School</span>
                  <span className="px-2 py-0.5 rounded bg-cyan-400/10 border border-cyan-400/20 text-[10px]">EdTech &amp; Growth</span>
                </div>
                <h5 className="font-heading font-bold text-white text-base group-hover:text-cyan-300 transition-colors mb-2">
                  From SEO Traffic to Student Growth (-60% Refunds)
                </h5>
                <p className="text-xs text-slate-400 line-clamp-2">
                  Scaling course-intent traffic by +35%, slashing refund rates by 60%, and automating email journeys with ConvertKit.
                </p>
              </div>
              <div className="mt-4 flex items-center gap-1.5 text-xs font-mono text-cyan-400 font-semibold">
                <span>Read Case Study</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

          </div>
        </section>

        {/* AUTHOR BIO & CTA CARD */}
        <div className="p-6 sm:p-8 rounded-3xl bg-gradient-to-b from-[#0e1626] to-[#080c16] border border-white/10 shadow-2xl flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4 text-left">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 via-indigo-600 to-cyan-400 text-white flex items-center justify-center font-bold text-lg flex-shrink-0 shadow-lg shadow-blue-500/20">
              KN
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-heading font-bold text-lg text-white">Kanna Narendra Kumar</h4>
                <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
                  Open for Work
                </span>
              </div>
              <p className="text-xs sm:text-sm text-slate-400 mt-1 max-w-md">
                Senior SEO Specialist &amp; AI Search Strategist with 8+ years scaling organic pipeline and leads for B2B SaaS companies.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-start md:justify-end">
            <Link
              to="/"
              className="px-4 py-2.5 rounded-xl bg-white/[0.06] hover:bg-white/[0.12] text-xs font-mono text-white transition-colors border border-white/5 flex items-center gap-2"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back to Portfolio</span>
            </Link>
            <a
              href="mailto:narendrakumarkanna@gmail.com"
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-xs font-mono text-white font-semibold transition-all shadow-md shadow-blue-500/20 flex items-center gap-2"
            >
              <span>Contact Me</span>
              <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

      </main>

      {/* Simple Footer */}
      <footer className="border-t border-white/[0.08] py-10 px-4 text-center text-xs font-mono text-slate-500">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>© {new Date().getFullYear()} Kanna Narendra Kumar · narendradigital.in</p>
          <div className="flex items-center gap-4 text-[11px] text-slate-400">
            <Link to="/" className="hover:text-white transition-colors">Portfolio Home</Link>
            <span>•</span>
            <a href="mailto:narendrakumarkanna@gmail.com" className="hover:text-white transition-colors">Contact</a>
            <span>•</span>
            <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="hover:text-white transition-colors">
              Back to Top ↑
            </button>
          </div>
        </div>
      </footer>

    </div>
  );
}
